import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useAppNavigate } from "../lib/navigate";
import {
  AlertCircle,
  ArrowLeft,
  Check,
  LoaderCircle,
  MessageCircle,
  Pencil,
  Plus,
  Star,
  Trash2,
  X,
} from "lucide-react";
import api from "../lib/api";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function initialsFromCustomer(c) {
  const src = `${c?.name || ""} ${c?.surname || ""}`.trim();
  if (!src) return "U";
  return src.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase() || "").join("") || "U";
}

function toWhatsAppLink(phone) {
  const digits = String(phone || "").replace(/\D+/g, "");
  return digits ? `https://wa.me/${digits}` : "";
}

const inputClass =
  "w-full rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-2.5 text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:border-[#94a3b8] focus:outline-none disabled:bg-[#f1f5f9] disabled:text-[#94a3b8]";
const labelClass = "block text-xs font-semibold text-[#475569] mb-1";

// ---------------------------------------------------------------------------
// AddressForm — inline form for add/edit
// ---------------------------------------------------------------------------
function AddressForm({ initial = {}, onSave, onCancel, primaryColor }) {
  const [form, setForm] = useState({
    label: initial.label || "",
    street: initial.street || "",
    city: initial.city || "",
    province: initial.province || "",
    zip: initial.zip || "",
    country: initial.country || "IT",
    isDefault: initial.isDefault || false,
  });

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  const canSave = form.street.trim() && form.city.trim() && form.zip.trim();

  return (
    <div className="space-y-3 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-4">
      <div className="space-y-1">
        <label className={labelClass}>Etichetta</label>
        <input
          type="text"
          value={form.label}
          onChange={(e) => set("label", e.target.value)}
          placeholder="es. Casa, Ufficio…"
          className={inputClass}
        />
      </div>

      <div className="space-y-1">
        <label className={labelClass}>Via / Indirizzo *</label>
        <input
          type="text"
          value={form.street}
          onChange={(e) => set("street", e.target.value)}
          placeholder="Via Roma 1"
          className={inputClass}
          required
        />
      </div>

      <div className="flex gap-2">
        <div className="flex-1 space-y-1">
          <label className={labelClass}>Città *</label>
          <input
            type="text"
            value={form.city}
            onChange={(e) => set("city", e.target.value)}
            placeholder="Milano"
            className={inputClass}
            required
          />
        </div>
        <div className="w-24 space-y-1">
          <label className={labelClass}>CAP *</label>
          <input
            type="text"
            value={form.zip}
            onChange={(e) => set("zip", e.target.value)}
            placeholder="20100"
            className={inputClass}
            required
          />
        </div>
      </div>

      <div className="flex gap-2">
        <div className="flex-1 space-y-1">
          <label className={labelClass}>Provincia</label>
          <input
            type="text"
            value={form.province}
            onChange={(e) => set("province", e.target.value)}
            placeholder="MI"
            maxLength={3}
            className={inputClass}
          />
        </div>
        <div className="flex-1 space-y-1">
          <label className={labelClass}>Paese</label>
          <input
            type="text"
            value={form.country}
            onChange={(e) => set("country", e.target.value)}
            placeholder="IT"
            maxLength={3}
            className={inputClass}
          />
        </div>
      </div>

      <label className="flex cursor-pointer items-center gap-2 text-sm text-[#334155]">
        <input
          type="checkbox"
          checked={form.isDefault}
          onChange={(e) => set("isDefault", e.target.checked)}
          className="h-4 w-4 rounded border-[#e2e8f0]"
        />
        Imposta come indirizzo principale
      </label>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onSave({ ...initial, ...form })}
          disabled={!canSave}
          className="flex flex-1 items-center justify-center gap-1 rounded-xl py-2.5 text-sm font-semibold text-white disabled:opacity-50"
          style={{ backgroundColor: primaryColor }}
        >
          <Check className="h-4 w-4" />
          Salva
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center justify-center gap-1 rounded-xl border border-[#e2e8f0] px-4 py-2.5 text-sm font-semibold text-[#64748b]"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddressCard — single address row
// ---------------------------------------------------------------------------
function AddressCard({ address, onEdit, onDelete, onSetDefault, canDelete }) {
  return (
    <div className="rounded-xl border border-[#e2e8f0] bg-white p-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="mb-0.5 flex flex-wrap items-center gap-1.5">
            {address.label && (
              <span className="text-sm font-semibold text-[#0f172a]">{address.label}</span>
            )}
            {address.isDefault && (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#f0fdf4] px-2 py-0.5 text-[11px] font-semibold text-[#16a34a]">
                <Star className="h-3 w-3 fill-[#16a34a]" />
                Principale
              </span>
            )}
          </div>
          <p className="text-sm text-[#334155]">{address.street}</p>
          <p className="text-xs text-[#64748b]">
            {[address.zip, address.city, address.province].filter(Boolean).join(" – ")}
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          {!address.isDefault && (
            <button
              type="button"
              onClick={onSetDefault}
              title="Imposta come principale"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#94a3b8] hover:text-[#0f172a]"
            >
              <Star className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={onEdit}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#64748b] hover:text-[#0f172a]"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            disabled={!canDelete}
            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-[#e2e8f0] text-[#ef4444] disabled:opacity-30"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProfilePage
// ---------------------------------------------------------------------------
export default function ProfilePage() {
  const { tenantId = "default" } = useParams();
  const navigate = useAppNavigate();

  const [loading, setLoading] = useState(true);
  const [pageError, setPageError] = useState("");
  const [customer, setCustomer] = useState(null);
  const [storeInfo, setStoreInfo] = useState({});
  const [primaryColor, setPrimaryColor] = useState("#0f172a");

  // --- personal info edit --------------------------------------------------
  const [editingInfo, setEditingInfo] = useState(false);
  const [infoForm, setInfoForm] = useState({ name: "", surname: "", email: "", companyName: "", vatNumber: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [infoError, setInfoError] = useState("");

  // --- addresses -----------------------------------------------------------
  const [editingAddressId, setEditingAddressId] = useState(null); // null | "new" | address.id
  const [savingAddresses, setSavingAddresses] = useState(false);
  const [addressError, setAddressError] = useState("");

  // Load profile
  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      setPageError("");
      try {
        const [meRes, cfgRes] = await Promise.all([
          api.get("/webapp/customers/me", { headers: { "x-tenant-id": tenantId } }),
          api.get("/webapp/config", { headers: { "x-tenant-id": tenantId } }),
        ]);
        if (!active) return;

        const me = meRes?.data?.data || {};
        if (me.guest || !me.customer) { navigate(`/${tenantId}`, { replace: true }); return; }

        setCustomer(me.customer);
        setStoreInfo(cfgRes?.data?.data?.storeInfo || {});
        const pc = cfgRes?.data?.data?.branding?.primaryColor;
        if (pc) setPrimaryColor(pc);
      } catch (err) {
        if (!active) return;
        setPageError(err?.response?.data?.error?.message || "Impossibile caricare il profilo.");
      } finally {
        if (active) setLoading(false);
      }
    }
    load();
    return () => { active = false; };
  }, [tenantId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync infoForm when customer loads
  useEffect(() => {
    if (customer) {
      setInfoForm({
        name: customer.name || "",
        surname: customer.surname || "",
        email: customer.email || "",
        companyName: customer.companyName || "",
        vatNumber: customer.vatNumber || "",
      });
    }
  }, [customer]);

  const whatsappLink = useMemo(() => toWhatsAppLink(storeInfo?.phone), [storeInfo?.phone]);
  const initials = useMemo(() => initialsFromCustomer(customer), [customer]);
  const isB2B = customer?.type === "b2b";

  // --- personal info save --------------------------------------------------
  async function saveInfo() {
    setSavingInfo(true);
    setInfoError("");
    try {
      const { data } = await api.patch("/webapp/customers/me", {
        name: infoForm.name.trim(),
        surname: infoForm.surname.trim(),
        email: infoForm.email.trim() || "",
        ...(isB2B ? { companyName: infoForm.companyName.trim(), vatNumber: infoForm.vatNumber.trim() } : {}),
      }, { headers: { "x-tenant-id": tenantId } });
      setCustomer(data.data.customer);
      setEditingInfo(false);
    } catch (err) {
      setInfoError(err?.response?.data?.error?.message || "Errore nel salvataggio.");
    } finally {
      setSavingInfo(false);
    }
  }

  // --- addresses helpers ---------------------------------------------------
  async function saveAddresses(newAddresses) {
    setSavingAddresses(true);
    setAddressError("");
    try {
      const { data } = await api.patch(
        "/webapp/customers/me",
        { addresses: newAddresses },
        { headers: { "x-tenant-id": tenantId } }
      );
      setCustomer(data.data.customer);
      setEditingAddressId(null);
    } catch (err) {
      setAddressError(err?.response?.data?.error?.message || "Errore nel salvataggio indirizzo.");
    } finally {
      setSavingAddresses(false);
    }
  }

  function handleAddressAdd(formData) {
    const current = customer?.addresses || [];
    saveAddresses([...current, formData]);
  }

  function handleAddressUpdate(id, formData) {
    const newList = (customer?.addresses || []).map((a) =>
      a.id === id ? { ...a, ...formData, id } : a
    );
    saveAddresses(newList);
  }

  function handleAddressDelete(id) {
    const newList = (customer?.addresses || []).filter((a) => a.id !== id);
    if (newList.length > 0 && !newList.some((a) => a.isDefault)) newList[0].isDefault = true;
    saveAddresses(newList);
  }

  function handleSetDefault(id) {
    const newList = (customer?.addresses || []).map((a) => ({ ...a, isDefault: a.id === id }));
    saveAddresses(newList);
  }

  // -------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-[#efefef]">
      <div className="mx-auto min-h-screen w-full max-w-[800px] bg-white shadow-sm">

        {/* Header */}
        <header className="sticky top-0 z-20 border-b border-[#e2e8f0] bg-white px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(`/${tenantId}`)}
              className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#d7e0ea] bg-white text-[#334155]"
              aria-label="Torna allo shop"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
            <h1 className="text-base font-semibold text-[#0f172a]">Il mio profilo</h1>
            <div className="w-9" />
          </div>
        </header>

        <main className="space-y-3 px-4 py-4">
          {/* Loading */}
          {loading && (
            <div className="flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-[#f8fafc] px-3 py-3 text-sm text-[#64748b]">
              <LoaderCircle className="h-4 w-4 animate-spin" />
              Caricamento profilo…
            </div>
          )}

          {/* Page-level error */}
          {pageError && (
            <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-600">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {pageError}
            </div>
          )}

          {!loading && customer && (
            <>
              {/* ── Avatar ── */}
              <section className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-4">
                <div className="flex items-center gap-3">
                  <div
                    className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full text-lg font-bold text-white"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-base font-semibold text-[#0f172a]">
                      {[customer.name, customer.surname].filter(Boolean).join(" ") || "Cliente"}
                    </p>
                    <p className="text-xs text-[#64748b]">{isB2B ? "Azienda" : "Privato"}</p>
                    <p className="mt-0.5 font-mono text-xs text-[#94a3b8]">+{customer.phone}</p>
                  </div>
                </div>
              </section>

              {/* ── Dati personali ── */}
              <section className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#0f172a]">Dati personali</h2>
                  {!editingInfo && (
                    <button
                      type="button"
                      onClick={() => { setEditingInfo(true); setInfoError(""); }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 text-xs font-semibold text-[#64748b]"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Modifica
                    </button>
                  )}
                </div>

                {!editingInfo ? (
                  <dl className="space-y-1.5 text-sm">
                    <div className="flex gap-2">
                      <dt className="w-28 shrink-0 text-[#64748b]">Nome</dt>
                      <dd className="font-medium text-[#0f172a]">{customer.name || "—"}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="w-28 shrink-0 text-[#64748b]">Cognome</dt>
                      <dd className="font-medium text-[#0f172a]">{customer.surname || "—"}</dd>
                    </div>
                    <div className="flex gap-2">
                      <dt className="w-28 shrink-0 text-[#64748b]">Email</dt>
                      <dd className="font-medium text-[#0f172a]">{customer.email || "—"}</dd>
                    </div>
                    {isB2B && (
                      <>
                        <div className="flex gap-2">
                          <dt className="w-28 shrink-0 text-[#64748b]">Rag. Sociale</dt>
                          <dd className="font-medium text-[#0f172a]">{customer.companyName || "—"}</dd>
                        </div>
                        <div className="flex gap-2">
                          <dt className="w-28 shrink-0 text-[#64748b]">P. IVA</dt>
                          <dd className="font-medium text-[#0f172a]">{customer.vatNumber || "—"}</dd>
                        </div>
                      </>
                    )}
                  </dl>
                ) : (
                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <div className="flex-1 space-y-1">
                        <label className={labelClass}>Nome *</label>
                        <input
                          type="text"
                          value={infoForm.name}
                          onChange={(e) => setInfoForm((f) => ({ ...f, name: e.target.value }))}
                          className={inputClass}
                          required
                        />
                      </div>
                      <div className="flex-1 space-y-1">
                        <label className={labelClass}>Cognome *</label>
                        <input
                          type="text"
                          value={infoForm.surname}
                          onChange={(e) => setInfoForm((f) => ({ ...f, surname: e.target.value }))}
                          className={inputClass}
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-1">
                      <label className={labelClass}>Email</label>
                      <input
                        type="email"
                        value={infoForm.email}
                        onChange={(e) => setInfoForm((f) => ({ ...f, email: e.target.value }))}
                        placeholder="mario@example.com"
                        className={inputClass}
                      />
                    </div>

                    {isB2B && (
                      <>
                        <div className="space-y-1">
                          <label className={labelClass}>Ragione Sociale *</label>
                          <input
                            type="text"
                            value={infoForm.companyName}
                            onChange={(e) => setInfoForm((f) => ({ ...f, companyName: e.target.value }))}
                            className={inputClass}
                            required
                          />
                        </div>
                        <div className="space-y-1">
                          <label className={labelClass}>Partita IVA *</label>
                          <input
                            type="text"
                            value={infoForm.vatNumber}
                            onChange={(e) => setInfoForm((f) => ({ ...f, vatNumber: e.target.value }))}
                            className={inputClass}
                            required
                          />
                        </div>
                      </>
                    )}

                    {infoError && (
                      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                        {infoError}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={saveInfo}
                        disabled={savingInfo || !infoForm.name.trim() || !infoForm.surname.trim()}
                        className="flex flex-1 items-center justify-center gap-1.5 rounded-xl py-2.5 text-sm font-bold text-white disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                      >
                        {savingInfo ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        Salva
                      </button>
                      <button
                        type="button"
                        onClick={() => { setEditingInfo(false); setInfoError(""); }}
                        className="rounded-xl border border-[#e2e8f0] px-4 text-sm font-semibold text-[#64748b]"
                      >
                        Annulla
                      </button>
                    </div>
                  </div>
                )}
              </section>

              {/* ── Credito ── */}
              {((customer.credit?.balance ?? 0) > 0 || (customer.welfareCredit?.balance ?? 0) > 0) && (
                <section className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-4">
                  <h2 className="mb-3 text-sm font-semibold text-[#0f172a]">Il mio credito</h2>
                  <div className="grid grid-cols-2 gap-3">
                    {(customer.credit?.balance ?? 0) > 0 && (
                      <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-3 text-center">
                        <p
                          className="text-xl font-bold"
                          style={{ color: primaryColor }}
                        >
                          €{Number(customer.credit.balance).toFixed(2)}
                        </p>
                        <p className="mt-0.5 text-[11px] font-medium text-[#64748b]">Credito negozio</p>
                      </div>
                    )}
                    {(customer.welfareCredit?.balance ?? 0) > 0 && (
                      <div className="rounded-xl border border-[#e2e8f0] bg-[#f8fafc] p-3 text-center">
                        <p
                          className="text-xl font-bold"
                          style={{ color: primaryColor }}
                        >
                          €{Number(customer.welfareCredit.balance).toFixed(2)}
                        </p>
                        <p className="mt-0.5 text-[11px] font-medium text-[#64748b]">Credito welfare</p>
                        {(customer.welfareCredit?.monthlyLimit ?? 0) > 0 && (
                          <p className="text-[10px] text-[#94a3b8]">
                            su €{Number(customer.welfareCredit.monthlyLimit).toFixed(0)}/mese
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ── Indirizzi ── */}
              <section className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-4">
                <div className="mb-3 flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-[#0f172a]">
                    Indirizzi
                    {(customer.addresses || []).length > 0 && (
                      <span className="ml-1.5 rounded-full bg-[#f1f5f9] px-1.5 py-0.5 text-[11px] font-semibold text-[#64748b]">
                        {customer.addresses.length}
                      </span>
                    )}
                  </h2>
                  {editingAddressId !== "new" && (
                    <button
                      type="button"
                      onClick={() => { setEditingAddressId("new"); setAddressError(""); }}
                      className="inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#e2e8f0] px-3 text-xs font-semibold text-[#64748b]"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Aggiungi
                    </button>
                  )}
                </div>

                {savingAddresses && (
                  <div className="mb-2 flex items-center gap-2 text-xs text-[#64748b]">
                    <LoaderCircle className="h-3.5 w-3.5 animate-spin" />
                    Salvataggio…
                  </div>
                )}

                {addressError && (
                  <div className="mb-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
                    <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                    {addressError}
                  </div>
                )}

                <div className="space-y-2">
                  {(customer.addresses || []).map((addr) =>
                    editingAddressId === addr.id ? (
                      <AddressForm
                        key={addr.id}
                        initial={addr}
                        primaryColor={primaryColor}
                        onSave={(formData) => handleAddressUpdate(addr.id, formData)}
                        onCancel={() => setEditingAddressId(null)}
                      />
                    ) : (
                      <AddressCard
                        key={addr.id}
                        address={addr}
                        canDelete={(customer.addresses || []).length > 1}
                        onEdit={() => { setEditingAddressId(addr.id); setAddressError(""); }}
                        onDelete={() => handleAddressDelete(addr.id)}
                        onSetDefault={() => handleSetDefault(addr.id)}
                      />
                    )
                  )}

                  {(customer.addresses || []).length === 0 && editingAddressId !== "new" && (
                    <p className="py-2 text-center text-sm text-[#94a3b8]">
                      Nessun indirizzo salvato.
                    </p>
                  )}

                  {editingAddressId === "new" && (
                    <AddressForm
                      primaryColor={primaryColor}
                      onSave={handleAddressAdd}
                      onCancel={() => setEditingAddressId(null)}
                    />
                  )}
                </div>
              </section>

              {/* ── Supporto
              {whatsappLink && (
                <section className="rounded-xl border border-[#e2e8f0] bg-white px-4 py-4">
                  <a
                    href={whatsappLink}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex h-10 items-center gap-2 rounded-xl border border-[#d7e0ea] px-3 text-sm font-medium text-[#0f172a]"
                  >
                    <MessageCircle className="h-4 w-4" />
                    Contattaci su WhatsApp
                  </a>
                </section>
              )}
              ── */}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
